import React from 'react';
import { receiptState, prepareReceipts, receiptKey } from '../../lib/receiptState';

// GOV13 receipts polish — presentation-only Purchase Receipts table (one-time add-on / credit-pack / usage-pack
// purchases; DISTINCT from base-plan subscription invoices). Renders STRICTLY from the backend receipt projection:
// truthful per-row state (paid+fulfilled vs paid-pending vs failed vs refunded — never a blanket green "PAID"),
// deterministic dedup+sort (no duplicate rows after polling), amounts from the backend only, a Download action
// ONLY when the backend says a document is available (never a broken link or a raw object path), and a Stripe
// TEST indicator in staging. It creates NO receipt and performs NO fetch.
const TONE = {
  green: 'bg-green-100 text-green-700', blue: 'bg-blue-100 text-blue-800', amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700', purple: 'bg-purple-100 text-purple-700',
};
const money = (amt, cur = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format(amt || 0);

// Domain A — a receipt is refund-requestable when it's a paid+fulfilled Servisfy commercial charge and no refund
// request is already open/complete for it. The BUTTON only requests; SuperAdmin (Tenant360) approves + executes.
const REFUNDABLE_TYPES = new Set(['PAID_ADDON', 'BASE_PLAN', 'CREDIT_PACK', 'USAGE_PACK']);
const REFUND_STATE_TONE = {
  REQUESTED: 'amber', UNDER_REVIEW: 'blue', APPROVED: 'blue', EXECUTING: 'amber', PROVIDER_PENDING: 'amber',
  PARTIALLY_REFUNDED: 'purple', REFUNDED: 'green', RECONCILED: 'green', REJECTED: 'red', FAILED: 'red',
};

export default function PurchaseReceiptsTable({ receipts = [], onDownload, downloadingKey = null, forbidden = false,
  onRequestRefund = null, refundByInvoice = {}, requestingRefundKey = null, t = (k) => k }) {
  if (forbidden) {
    return <p className="text-sm text-gray-500" data-testid="receipts-forbidden">Purchase receipts are not visible for your role.</p>;
  }
  const rows = prepareReceipts(receipts);
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500" data-testid="receipts-empty">No purchase receipts yet. One-time add-on, credit-pack and usage-pack purchases appear here once paid.</p>;
  }
  const showRefundCol = typeof onRequestRefund === 'function';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="commercial-invoices-table">
        <thead className="text-xs text-gray-500 uppercase border-b">
          <tr>{['Receipt', 'Date', 'Type', 'Product', 'Subtotal', 'Tax', 'Total', 'Status', 'Receipt', ...(showRefundCol ? ['Refund'] : [])].map((h) => (
            <th key={h} className="text-left py-2 px-2">{h}</th>))}</tr>
        </thead>
        <tbody>
          {rows.map((inv) => {
            const key = receiptKey(inv);
            const st = receiptState(inv);
            return (
              <tr key={key} className="border-b last:border-0" data-testid={`commercial-invoice-${inv.invoice_number}`}>
                <td className="py-2 px-2 font-mono text-xs">{inv.invoice_number}</td>
                <td className="py-2 px-2">{String(inv.paid_at || inv.issued_at || '').slice(0, 10)}</td>
                <td className="py-2 px-2 capitalize">{String(inv.transaction_type || '').replace(/_/g, ' ')}</td>
                <td className="py-2 px-2">
                  <div className="font-medium text-gray-800">{inv.description || inv.product_key}</div>
                  {inv.product_version && <div className="text-[10px] text-gray-400">{inv.product_version}</div>}
                  {inv.quantity != null && <div className="text-xs text-gray-400">qty {inv.quantity}</div>}
                </td>
                <td className="py-2 px-2">{money(inv.subtotal, inv.currency)}</td>
                <td className="py-2 px-2">
                  <div>{money(inv.tax_amount, inv.currency)}</div>
                  {(inv.tax_name || inv.tax_rate != null) && (
                    <div className="text-xs text-gray-400">{inv.tax_name || 'Tax'}{inv.tax_rate != null ? ` ${Math.round(inv.tax_rate * 100)}%` : ''}</div>
                  )}
                </td>
                <td className="py-2 px-2 font-medium">{money(inv.total, inv.currency)}</td>
                <td className="py-2 px-2">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE[st.tone]}`}
                      data-testid={`receipt-state-${inv.invoice_number}`} data-state={st.state}>{st.label}</span>
                    {st.state === 'FULFILMENT_FAILED' && (
                      <span className="text-[10px] text-red-600" data-testid={`receipt-fulfilment-help-${inv.invoice_number}`}>
                        Payment received — our team is completing fulfilment. Contact support if this persists.</span>
                    )}
                    {inv.mode === 'test' && (
                      <span className="text-[10px] text-amber-600" data-testid={`commercial-invoice-test-${inv.invoice_number}`}>Stripe TEST</span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-2">
                  {inv.document_available ? (
                    <button type="button" disabled={downloadingKey === key} onClick={() => onDownload && onDownload(inv)}
                      data-testid={`download-commercial-invoice-${inv.invoice_number}-btn`}
                      className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">
                      {downloadingKey === key ? 'Downloading…' : 'Download receipt'}
                    </button>
                  ) : st.claimsFulfilled ? (
                    // A paid + fulfilled purchase must NEVER show a bare dash — explain the document state truthfully.
                    <span className="text-xs text-amber-700" data-testid={`receipt-doc-unavailable-${inv.invoice_number}`}>
                      Receipt unavailable — contact support
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400" data-testid={`receipt-no-doc-${inv.invoice_number}`}>—</span>
                  )}
                </td>
                {showRefundCol && (() => {
                  const rf = refundByInvoice[inv.invoice_number];
                  const refundable = st.claimsFulfilled && REFUNDABLE_TYPES.has(String(inv.transaction_type || '').toUpperCase());
                  // A rejected/cancelled prior request is terminal but leaves the charge refundable → the tenant may
                  // RE-REQUEST. Active states (requested/review/approved/executing/partial) and a completed refund
                  // (refunded/reconciled) do NOT show a request button.
                  const reRequestable = !rf || rf.state === 'REJECTED' || rf.state === 'CANCELLED';
                  const stateBtn = requestingRefundKey === inv.invoice_number;
                  const pill = (s) => (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE[REFUND_STATE_TONE[s] || 'blue']}`}
                      data-testid={`refund-state-${inv.invoice_number}`}>{String(s).replace(/_/g, ' ')}</span>
                  );
                  if (rf && !reRequestable) return <td className="py-2 px-2">{pill(rf.state)}</td>;
                  if (refundable) return (
                    <td className="py-2 px-2">
                      <div className="flex flex-col gap-1 items-start">
                        {rf && pill(rf.state)}
                        <button type="button" disabled={stateBtn} onClick={() => onRequestRefund(inv)}
                          data-testid={`request-refund-${inv.invoice_number}-btn`}
                          className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">
                          {stateBtn ? 'Requesting…' : (rf ? 'Request again' : 'Request refund')}
                        </button>
                      </div>
                    </td>
                  );
                  return <td className="py-2 px-2">{rf ? pill(rf.state) : <span className="text-xs text-gray-400">—</span>}</td>;
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
